import { getElectronAPI } from "@/modules/electron/api";
import { URL } from "url";
import {
  CalendarEvent,
  RecallMeetingDetectedEventKind,
  RecallPermissionKind,
  RecallPermissionStatus,
  ElectronAppInfo,
  RecallSdkState,
  RecallSdkStatePayload,
  ImportAppleNotesResult,
  GetAppleNoteContentResult,
  MenuActionEvent,
  DesktopShortcutEvent,
  DesktopAudioWaveformUpdateEvent,
  StartDesktopAudioUploadArgs,
  StartDesktopAudioUploadResult,
  DesktopAudioRecordingRef,
  DesktopRemoveAudioArtifactArgs,
  DesktopAudioArtifactUploadFailureDebugInfo,
  DesktopAudioArtifactRef,
  DesktopAudioArtifactSnapshot,
  DesktopAudioRecordingStrategy,
  DesktopAudioUploadProgressEvent,
  DesktopAudioUploadCompletedEvent,
  DesktopAudioUploadErrorEvent,
  DesktopRecordingStartAttemptTrace,
  DesktopRecordingRecoveryState,
  OAuthAccessTokenGetRequestArgs,
  OAuthAccessTokenGetResponseArgs,
  OAuthAccessTokenSetArgs,
  AiToolkitRequestArgs,
  AiToolkitResponseArgs,
  WorkingContextMatch,
  WorkingContextAddSubscriptionArgs,
  WorkingContextRemoveSubscriptionArgs,
  WorkingContextSnapshot,
  WorkingContextStatus,
  StartRecordingOutcome,
  StartRecordingOutcomeKind,
} from "@/modules/electron/types";
import { ThemeType } from "@/mds/themes/theme-context";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { TrackedEvent } from "@/domains/metrics";

export const electronBindingNavigateToInternalUrl = async ({ url }: { url: URL }) => {
  const electronApi = getElectronAPI();

  await electronApi?.navigateToInternalUrl({ url: url.toString() });

  return null;
};

export const electronBindingNavigateToExternalUrl = async ({ url }: { url: URL }) => {
  const electronApi = getElectronAPI();

  await electronApi?.navigateToExternalUrl({ url: url.toString() });

  return null;
};

export const electronBindingToggleMenu = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.toggleMenu();
};

export const electronBindingMinimizeWindow = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.minimizeWindow();
};

export const electronBindingMaximizeWindow = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.maximizeWindow();
};

export const electronBindingCloseWindow = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.closeWindow();
};

export const electronBindingSetOAuthAccessToken = async ({
  token,
}: OAuthAccessTokenSetArgs): Promise<void> => {
  const electronApi = getElectronAPI();

  await electronApi?.oauthAccessToken?.set({ token });
};

export const electronBindingClearOAuthAccessToken = async (): Promise<void> => {
  const electronApi = getElectronAPI();

  await electronApi?.oauthAccessToken?.clear();
};

export const electronBindingOnOAuthAccessTokenGetRequested = async ({
  callback,
}: {
  callback: (args: OAuthAccessTokenGetRequestArgs) => void;
}) => {
  const electronApi = getElectronAPI();

  return electronApi?.oauthAccessToken?.onGetRequested?.(callback);
};

export const electronBindingRespondToOAuthAccessTokenGetRequest = async ({
  requestId,
  token,
}: OAuthAccessTokenGetResponseArgs): Promise<void> => {
  const electronApi = getElectronAPI();

  await electronApi?.oauthAccessToken?.respondToGetRequest?.({ requestId, token });
};

export const electronBindingOnOAuthAccessTokenRefreshRequested = async ({
  callback,
}: {
  callback: () => void;
}) => {
  const electronApi = getElectronAPI();

  return electronApi?.oauthAccessToken?.onRefreshRequested?.(callback);
};

export const electronBindingOnAiToolkitRequested = async ({
  callback,
}: {
  callback: (args: AiToolkitRequestArgs) => void;
}) => {
  const electronApi = getElectronAPI();

  return electronApi?.aiToolkit?.onRequest?.(callback);
};

export const electronBindingRespondToAiToolkitRequest = async (
  args: AiToolkitResponseArgs
): Promise<void> => {
  const electronApi = getElectronAPI();

  await electronApi?.aiToolkit?.respondToRequest?.(args);
};

interface ElectronBindingUpdateNowOptions {
  waitForUpdate?: boolean;
}
export const electronBindingUpdateNow = ({
  waitForUpdate = false,
}: ElectronBindingUpdateNowOptions) => {
  const electronApi = getElectronAPI();

  return electronApi?.updateNow(waitForUpdate);
};

export const electronBindingRestartApp = async () => {
  const electronApi = getElectronAPI();

  if (electronApi?.restartApp) {
    await electronApi.restartApp();
  }
};

export const electronBindingOpenSystemSoundSettings = async ({
  focus,
}: {
  focus?: "input" | "output";
} = {}) => {
  const electronApi = getElectronAPI();
  await electronApi?.openSystemSoundSettings?.({ focus });
};

export const electronBindingSetTheme = async ({ theme }: { theme: ThemeType }) => {
  const electronApi = getElectronAPI();
  await electronApi?.setTheme({ theme });
};

export const electronBindingSetCalendarNotificationEnabled = async ({
  enabled,
}: {
  enabled: boolean;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setCalendarNotificationEnabled?.({ enabled });
};

export const electronBindingGetCalendarNotificationEnabled = async (): Promise<{
  enabled: boolean;
}> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.getCalendarNotificationEnabled?.()) ?? { enabled: false };
};

export const electronBindingSetMeetingNotificationEnabled = async ({
  enabled,
}: {
  enabled: boolean;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setMeetingNotificationEnabled?.({ enabled });
};

export const electronBindingGetMeetingNotificationEnabled = async (): Promise<{
  enabled: boolean;
}> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.getMeetingNotificationEnabled?.()) ?? { enabled: false };
};

export const electronBindingSetAdvancedMeetingDetectionShown = async ({
  shown,
}: {
  shown: boolean;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setAdvancedMeetingDetectionShown?.({ shown });
};

export const electronBindingGetAdvancedMeetingDetectionShown = async (): Promise<{
  shown: boolean;
}> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.getAdvancedMeetingDetectionShown?.()) ?? { shown: false };
};

export const electronBindingSetTeamsMeetingDetectionEnabled = async ({
  enabled,
}: {
  enabled: boolean;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setTeamsMeetingDetectionEnabled?.({ enabled });
};

export const electronBindingGetTeamsMeetingDetectionEnabled = async (): Promise<{
  enabled: boolean;
}> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.getTeamsMeetingDetectionEnabled?.()) ?? { enabled: false };
};

type BrowserMetadata = {
  segmentAnonymousId?: string;
  gaClientId?: string;
  gaSessionId?: string;
};

export const electronBindingOnBrowserMetadata = async (
  callback: (metadata: BrowserMetadata) => Promise<void>
): Promise<() => void> => {
  const electronApi = getElectronAPI();

  try {
    if (!electronApi?.onBrowserMetadata) {
      return () => undefined;
    }

    const unsubscribe = await electronApi.onBrowserMetadata(async metadata => {
      await callback({
        segmentAnonymousId: metadata.segmentAnonymousId ?? undefined,
        gaClientId: metadata.gaClientId ?? undefined,
        gaSessionId: metadata.gaSessionId ?? undefined,
      });
    });

    return unsubscribe;
  } catch (error) {
    logger.warn({
      message: "[electronBindingOnBrowserMetadata] Failed to subscribe to browser metadata.",
      info: { error: objectModule.safeErrorAsJson(error as Error) },
    });
  }

  return () => undefined;
};

export const electronBindingSetCalendarEvents = async ({
  ongoingEvents,
  upcomingEvents,
}: {
  ongoingEvents: CalendarEvent[];
  upcomingEvents: CalendarEvent[];
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setCalendarEvents?.({ ongoingEvents, upcomingEvents });
};

export const electronBindingOnMenuActionEvent = async ({
  callback,
}: {
  callback: (payload: { actionEvent: MenuActionEvent }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.onMenuActionEvent?.(callback);
};

export const electronBindingOnDesktopShortcutEvent = async ({
  callback,
}: {
  callback: (payload: { shortcutEvent: DesktopShortcutEvent }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.onDesktopShortcutEvent?.(callback);
};

export const electronBindingOnTrackedEvent = async ({
  callback,
}: {
  callback: (payload: { trackedEvent: TrackedEvent }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.onTrackedEvent?.(callback);
};

export const electronBindingSetIsClientReady = async ({ isReady }: { isReady: boolean }) => {
  const electronApi = getElectronAPI();
  await electronApi?.setIsClientReady?.({ isReady });
};

// ==========================
// Recall.ai Desktop SDK Bindings
// ==========================

export const recallBindingPrepareDesktopRecording = async () => {
  const electronApi = getElectronAPI();
  return (await electronApi?.recall.prepareDesktopRecording()) || { windowId: "" };
};

export const recallBindingStartRecording = async (args: {
  audioRecordingId: string;
  windowId: string;
  uploadToken: string;
  recordingStrategy: DesktopAudioRecordingStrategy;
  calendarEventId?: string;
  startAttemptTrace?: DesktopRecordingStartAttemptTrace;
}): Promise<StartRecordingOutcome> => {
  const electronApi = getElectronAPI();
  if (!electronApi) {
    logger.error({
      message: "[electronBindingStartRecording] Electron API unavailable",
      info: { audioRecordingId: args.audioRecordingId },
    });
    return {
      outcome: StartRecordingOutcomeKind.Fatal,
      error: "Electron Recall API unavailable",
    };
  }

  return electronApi.recall.startRecording({
    audioRecordingId: args.audioRecordingId,
    windowId: args.windowId,
    uploadToken: args.uploadToken,
    recordingStrategy: args.recordingStrategy,
    calendarEventId: args.calendarEventId,
    startAttemptTrace: args.startAttemptTrace,
  });
};

export const recallBindingPauseRecording = async (args: { windowId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.pause({ windowId: args.windowId });
};

export const recallBindingResumeRecording = async (args: { windowId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.resume({ windowId: args.windowId });
};

export const recallBindingStopRecording = async (args: { windowId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.stop({ windowId: args.windowId });
};

export const recallBindingGetRecordingSession = async (args: { audioRecordingId: string }) => {
  const electronApi = getElectronAPI();
  return (
    electronApi?.recall.getRecordingSession({ audioRecordingId: args.audioRecordingId }) ?? null
  );
};

export const recallBindingGetRecordingRecoveryState = async (args: {
  audioRecordingId: string;
}): Promise<DesktopRecordingRecoveryState | null> => {
  const electronApi = getElectronAPI();
  return (
    electronApi?.recall.getRecordingRecoveryState({
      audioRecordingId: args.audioRecordingId,
    }) ?? null
  );
};

export const recallBindingPauseRecordingSession = async (args: { audioRecordingId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.pauseRecordingSession({ audioRecordingId: args.audioRecordingId });
};

export const recallBindingResumeRecordingSession = async (args: { audioRecordingId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.resumeRecordingSession({ audioRecordingId: args.audioRecordingId });
};

export const recallBindingStopRecordingSession = async (args: { audioRecordingId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.stopRecordingSession({ audioRecordingId: args.audioRecordingId });
};

export const recallBindingOnEvent = ({
  callback,
}: {
  callback: (payload: { event: unknown }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onEvent?.(callback);
};

export const recallBindingOnMeetingEvent = async ({
  callback,
}: {
  callback: (payload: {
    event: {
      window: { id: string; title?: string; url?: string; platform: string };
      info?: {
        audioRecordingId?: string;
        startTime?: string | null;
        endTime?: string | null;
        attendees?: string[] | null;
      };
    };
    kind: RecallMeetingDetectedEventKind;
    debug: boolean;
  }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onMeetingEvent?.(callback);
};

export const recallBindingGetPermissionStatus = async (): Promise<
  Record<RecallPermissionKind, RecallPermissionStatus>
> => {
  const electronApi = getElectronAPI();

  const status = await electronApi?.recall.getPermissionStatus();

  if (!status) {
    return {
      [RecallPermissionKind.Accessibility]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.ScreenCapture]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.Microphone]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.SystemAudio]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.FullDiskAccess]: RecallPermissionStatus.NotRequested,
    };
  }

  return status;
};

export const recallBindingRequestPermission = async ({
  permission,
}: {
  permission: RecallPermissionKind;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.requestPermission(permission);
};

export const recallBindingOpenPermissionPane = async ({
  permission,
}: {
  permission: RecallPermissionKind;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.openPermissionPane(permission);
};

export const recallBindingOnPermissionStatus = async ({
  callback,
}: {
  callback: (payload: {
    event: { permission: RecallPermissionKind; status: RecallPermissionStatus };
  }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onPermissionStatus?.(callback);
};

export const recallBindingGetSdkState = async () => {
  const electronApi = getElectronAPI();
  const payload = await electronApi?.recall.getSdkState();

  return (
    payload ?? {
      state: RecallSdkState.Idle,
    }
  );
};

export const recallBindingOnSdkState = async ({
  callback,
}: {
  callback: (payload: RecallSdkStatePayload) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onSdkState?.(callback);
};

export const recallBindingTestUnexpectedShutdown = async () => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.testUnexpectedShutdown?.();
};

export const electronBindingResetTccAndClearCache = async ({ appName }: { appName: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.resetTccAndClearCache?.({ appName });
};

export const electronBindingSetClientMetadata = async ({
  context,
}: {
  context: Record<string, unknown>;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setClientMetadata?.({ context });
};

export const electronBindingClearClientMetadata = async () => {
  const electronApi = getElectronAPI();
  await electronApi?.clearClientMetadata?.();
};

export const electronBindingGetElectronAppInfo = async (): Promise<ElectronAppInfo | null> => {
  const electronApi = getElectronAPI();
  const info = await electronApi?.getElectronAppInfo?.();
  return info ?? null;
};

export const electronBindingGetAudioArtifactRef = async (
  args: DesktopAudioRecordingRef
): Promise<DesktopAudioArtifactRef | null> => {
  const electronApi = getElectronAPI();

  return (await electronApi?.audioArtifacts.getAudioArtifactRef(args)) ?? null;
};

export const electronBindingGetAudioArtifactSnapshot = async (
  args: DesktopAudioRecordingRef
): Promise<DesktopAudioArtifactSnapshot | null> => {
  const electronApi = getElectronAPI();

  return (await electronApi?.audioArtifacts.getAudioArtifactSnapshot(args)) ?? null;
};

export const electronBindingGetAudioArtifactUploadFailureDebugInfo = async (
  args: DesktopAudioRecordingRef
): Promise<DesktopAudioArtifactUploadFailureDebugInfo | null> => {
  const electronApi = getElectronAPI();

  return (await electronApi?.audioArtifacts.getAudioArtifactUploadFailureDebugInfo(args)) ?? null;
};

export const electronBindingFinalizeAudioArtifact = async (
  args: DesktopAudioRecordingRef
): Promise<DesktopAudioArtifactRef | null> => {
  const electronApi = getElectronAPI();

  return (await electronApi?.audioArtifacts.finalizeAudioArtifact(args)) ?? null;
};

export const electronBindingRemoveAudioArtifact = async (
  args: DesktopRemoveAudioArtifactArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.audioArtifacts.removeAudioArtifact(args);
};

export const electronBindingStartAudioUpload = async (
  args: StartDesktopAudioUploadArgs
): Promise<StartDesktopAudioUploadResult> => {
  const electronApi = getElectronAPI();

  return (
    (await electronApi?.audioUpload.startAudioUpload(args)) ?? {
      success: false,
      error: "Desktop audio upload is unavailable",
    }
  );
};

export const electronBindingOnAudioUploadProgress = ({
  callback,
}: {
  callback: (payload: DesktopAudioUploadProgressEvent) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.audioUpload.onProgress?.(callback);
};

export const electronBindingOnAudioUploadCompleted = ({
  callback,
}: {
  callback: (payload: DesktopAudioUploadCompletedEvent) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.audioUpload.onCompleted?.(callback);
};

export const electronBindingOnAudioUploadError = ({
  callback,
}: {
  callback: (payload: DesktopAudioUploadErrorEvent) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.audioUpload.onError?.(callback);
};

export const electronBindingOnDesktopAudioWaveformUpdate = ({
  callback,
}: {
  callback: (payload: DesktopAudioWaveformUpdateEvent) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.audioWaveform.onUpdate?.(callback);
};

export const electronBindingGetWorkingContextStatus = async (): Promise<WorkingContextStatus> => {
  const electronApi = getElectronAPI();

  return (
    (await electronApi?.workingContext?.getStatus()) ?? {
      running: false,
      activeSubscriptionCount: 0,
      recentObservationCount: 0,
      recentMatchCount: 0,
      observationCapacity: 0,
      matchCapacity: 0,
    }
  );
};

export const electronBindingGetWorkingContextSnapshot =
  async (): Promise<WorkingContextSnapshot | null> => {
    const electronApi = getElectronAPI();

    return (await electronApi?.workingContext?.getSnapshot()) ?? null;
  };

export const electronBindingAddWorkingContextSubscription = async (
  args: WorkingContextAddSubscriptionArgs
): Promise<WorkingContextSnapshot | null> => {
  const electronApi = getElectronAPI();

  return (await electronApi?.workingContext?.addSubscription(args)) ?? null;
};

export const electronBindingRemoveWorkingContextSubscription = async (
  args: WorkingContextRemoveSubscriptionArgs
): Promise<WorkingContextSnapshot | null> => {
  const electronApi = getElectronAPI();

  return (await electronApi?.workingContext?.removeSubscription(args)) ?? null;
};

export const electronBindingOnWorkingContextMatch = ({
  callback,
}: {
  callback: (payload: WorkingContextMatch) => void;
}) => {
  const electronApi = getElectronAPI();

  return electronApi?.workingContext?.onMatch?.(callback);
};

export const electronBindingImportAppleNotes = async (options?: {
  collectAttachments?: boolean;
}): Promise<ImportAppleNotesResult> => {
  const electronApi = getElectronAPI();
  return (
    (await electronApi?.importAppleNotes?.(options)) ?? {
      success: false,
      error: "Not available",
      notes: [],
      notesPerTag: {},
    }
  );
};

export const electronBindingGetAppleNoteContent = async (
  noteId: number
): Promise<GetAppleNoteContentResult> => {
  const electronApi = getElectronAPI();
  const result = await electronApi?.getAppleNoteContent?.(noteId);
  if (!result) {
    throw new Error("getAppleNoteContent not available");
  }
  return result;
};

export const electronBindingCloseAppleNotesReader = async (): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.closeAppleNotesReader?.();
};
